<script setup>
import { themeConfig } from '@themeConfig'
import { useTrainerAccountStore } from '@/stores/trainerAccount'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const accountStore = useTrainerAccountStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const email = computed(() => String(route.query.email || ''))
const token = computed(() => String(route.query.confirmation_token || ''))
const statusMessage = ref('')
const formError = ref('')
const loading = ref(false)
const resendEmail = ref(email.value)

onMounted(async () => {
  if (!token.value)
    return

  loading.value = true
  formError.value = ''
  try {
    const result = await accountStore.confirmEmail(token.value)

    statusMessage.value = result.message
    if (result.trainer?.lifecycle_state === 'active')
      await router.replace('/')
    else if (result.trainer)
      await router.replace({ name: 'login', query: { confirmed: '1' } })
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || t('confirmEmail.genericError')
  }
  finally {
    loading.value = false
  }
})

const resend = async () => {
  formError.value = ''
  statusMessage.value = ''
  loading.value = true
  try {
    const result = await accountStore.resendConfirmation(resendEmail.value)

    statusMessage.value = result.message
  }
  catch (err) {
    formError.value = err.message || t('confirmEmail.resendError')
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <VCard
      flat
      border
      max-width="520"
      width="100%"
      class="pa-2"
    >
      <VCardText>
        <div class="d-flex align-center gap-x-3 mb-6">
          <VNodeRenderer :nodes="themeConfig.app.logo" />
          <span class="text-h5 text-high-emphasis">FitX</span>
        </div>

        <h1 class="text-h4 mb-1">
          {{ $t('confirmEmail.title') }}
        </h1>
        <p class="mb-0 text-medium-emphasis">
          {{ $t('confirmEmail.subtitle') }}
        </p>
      </VCardText>

      <VCardText>
        <VProgressLinear
          v-if="loading && token"
          indeterminate
          class="mb-4"
          :aria-label="$t('confirmEmail.confirming')"
        />

        <VAlert
          v-if="formError"
          type="error"
          variant="tonal"
          class="mb-4"
          role="alert"
        >
          {{ formError }}
        </VAlert>

        <VAlert
          v-if="statusMessage"
          type="success"
          variant="tonal"
          class="mb-4"
          role="status"
        >
          {{ statusMessage }}
        </VAlert>

        <VAlert
          v-if="!token"
          type="info"
          variant="tonal"
          class="mb-4"
          role="status"
        >
          {{ $t('confirmEmail.checkInbox') }}
        </VAlert>

        <div
          v-if="!token"
          class="d-flex flex-column gap-3"
        >
          <VTextField
            v-model="resendEmail"
            :label="$t('common.email')"
            type="email"
            autocomplete="username"
            :disabled="loading"
          />
          <VBtn
            :loading="loading"
            @click="resend"
          >
            {{ $t('confirmEmail.resendButton') }}
          </VBtn>
        </div>

        <div class="mt-6 text-center">
          <RouterLink
            class="text-primary"
            :to="{ name: 'login' }"
          >
            {{ $t('common.backToSignIn') }}
          </RouterLink>
        </div>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
